import React from 'react';
import { connect } from 'react-redux';
import { LinearProgress } from '@mui/material';
import { displayDuration } from '../../../utils/parsing';
import ReactEcharts from '../../lazy/Echarts';

const getOptions = (series, maxTime) => ({
  animation: false,
  grid: {
    top: 10,
    bottom: 20,
    left: 'top',
    right: 20,
    containLabel: true,
  },
  tooltip: {
    trigger: 'axis',
    formatter: (params) => {
      const { val, unit } = displayDuration(params[0].axisValue, 'minutes');
      let tooltip = `<div>${val} ${unit}`;
      params.forEach((p) => {
        tooltip += `<br /> ${p.marker}${p.seriesName}: ${(
          p.value[1] || 0
        ).toLocaleString()} points`;
      });
      tooltip += '</div>';
      return tooltip;
    },
  },
  xAxis: {
    type: 'value',
    name: 'Sample rate (minutes)',
    nameLocation: 'center',
    nameGap: 25,
    max: Math.ceil(maxTime) + 1,
    minInterval: 1,
    splitLine: { show: false },
  },
  yAxis: {
    type: 'log',
    name: 'Points',
    splitLine: { show: false },
  },
  color: ['#4d9de0'],
  series,
});

/**
 * A histogram of mean point sample rate, stacked per-building
 * Uses log scales for both axis to account for exponential nature of outliers
 */
const PointSampleRate = ({ series, maxTime, loaded, isEmpty }) =>
  isEmpty ? null : (
    <div className="data-quality-widget">
      {!loaded ? (
        <LinearProgress color="secondary" className="loading" />
      ) : (
        <ReactEcharts notMerge style={{ height: '220px' }} option={getOptions(series, maxTime)} />
      )}
    </div>
  );

export default connect((state, { buildings }) => {
  const {
    entities: {
      buildings: {
        byId: buildingsById,
        meta: { receivedAt: buildingsLoaded },
      },
      ingestStats: { loaded: ingestLoaded, pointSampleRates, pointSampleRatesLoaded },
      points: { byBuilding: pointsByBuildingId },
    },
  } = state;
  // loaded if either we have sample data or we've loaded ingest + buildings and there isn't any data
  const loaded =
    pointSampleRatesLoaded > 0 || (ingestLoaded && buildingsLoaded && buildings.length === 0);
  const isEmpty = Object.keys(pointSampleRates).length === 0 && loaded;

  // build up a dict-of-dicts of point count by sample rate and building id
  /* eslint-disable no-param-reassign */
  const pointAgesByBuilding = buildings.reduce((data, bid) => {
    data[bid] =
      (pointsByBuildingId[bid] ?? [])
        .map((pid) => pointSampleRates[pid])
        .filter((rate) => rate != null)
        .map((rateSeconds) => (rateSeconds / 60).toFixed(1)) // e.g. '4.2 mins'
        .reduce((acc, bucket) => {
          acc[bucket] = (acc[bucket] || 0) + 1;
          return acc;
        }, {}) || {};
    return data;
  }, {});
  /* eslint-enable */

  const series = buildings.map((bid) => ({
    name: buildingsById[bid]?.name,
    type: 'bar',
    stack: `${buildingsById[bid]?.orgId}`, // admin view will show a stack per organization
    data: Object.entries(pointAgesByBuilding[bid]).map(([ts, c]) => [parseFloat(ts), c]),
    barWidth: '5',
    barMinHeight: '5',
  }));

  const maxTime = series
    .map((s) => s.data)
    .flat()
    .map((e) => e[0])
    .reduce((m, t) => Math.max(m, t), 0);

  return { series, maxTime, isEmpty, loaded };
})(PointSampleRate);
